import {
  REWRITE_GATE_UNAVAILABLE_ERROR_CODE,
  REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE,
  REWRITE_INSTRUCTION_REFUSED_ERROR_CODE,
  REWRITE_INVALID_OUTPUT_ERROR_CODE,
} from '@artem-lex/compliance-documents'
import { WorkflowEventType, type WorkflowEvent } from './workflows'

export const HANDLED_REWRITE_ERROR_CODES = new Set<string>([
  REWRITE_INSUFFICIENT_SOURCES_ERROR_CODE,
  REWRITE_INSTRUCTION_REFUSED_ERROR_CODE,
  REWRITE_INVALID_OUTPUT_ERROR_CODE,
  REWRITE_GATE_UNAVAILABLE_ERROR_CODE,
])

export type RewriteInstructionSuccess = {
  ok: true
  result: string
  runId: string
}

export type RewriteInstructionHandledError = {
  ok: false
  code: string
  message?: string
  blockReason?: string
  availableSourceNames?: string[]
}

export type RewriteInstructionMutationResult =
  | RewriteInstructionSuccess
  | RewriteInstructionHandledError

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function readAvailableSourceNames(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const names = value.filter((entry): entry is string => typeof entry === 'string')
  return names.length > 0 ? names : undefined
}

export function isHandledRewriteErrorCode(code: string): boolean {
  return HANDLED_REWRITE_ERROR_CODES.has(code)
}

export function toHandledRewriteError(
  payload: Record<string, unknown>
): RewriteInstructionHandledError | null {
  const code = readOptionalString(payload.code)
  if (code == null || !isHandledRewriteErrorCode(code)) {
    return null
  }
  return {
    ok: false,
    code,
    ...(readOptionalString(payload.message) != null
      ? { message: readOptionalString(payload.message) }
      : {}),
    ...(readOptionalString(payload.blockReason) != null
      ? { blockReason: readOptionalString(payload.blockReason) }
      : {}),
    ...(readAvailableSourceNames(payload.availableSourceNames) != null
      ? {
          availableSourceNames: readAvailableSourceNames(
            payload.availableSourceNames
          ),
        }
      : {}),
  }
}

export function applyRewriteStreamEvent(
  event: WorkflowEvent,
  current: RewriteInstructionMutationResult | null
): RewriteInstructionMutationResult | null {
  if (event.type === WorkflowEventType.WorkflowCompleted) {
    if (!isRecord(event.result)) {
      return current
    }
    const handled = toHandledRewriteError(event.result)
    if (handled != null) {
      return handled
    }
    const resultText = readOptionalString(event.result.result)
    const runId =
      readOptionalString(event.result.runId) ??
      readOptionalString(
        'workflowRunId' in event ? event.workflowRunId : undefined
      )
    if (resultText == null || runId == null) {
      return current
    }
    return { ok: true, result: resultText, runId }
  }

  if (event.type === WorkflowEventType.WorkflowFailed) {
    const failedRecord = event as WorkflowEvent & {
      error?: string
      code?: string
    }
    const handled = toHandledRewriteError({
      code: failedRecord.code,
      message: failedRecord.error,
    })
    if (handled != null) {
      return handled
    }
    throw new Error(failedRecord.error ?? 'Workflow failed')
  }

  return current
}

export function parseRewriteSseBuffer(
  buffer: string,
  current: RewriteInstructionMutationResult | null
): { remaining: string; result: RewriteInstructionMutationResult | null } {
  const lines = buffer.split('\n')
  const remaining = lines.pop() ?? ''
  let result = current
  for (const line of lines) {
    if (!line.startsWith('data: ')) {
      continue
    }
    const data = line.slice(6).trim()
    if (data.length === 0) {
      continue
    }
    const event = JSON.parse(data) as WorkflowEvent
    result = applyRewriteStreamEvent(event, result)
  }
  return { remaining, result }
}
